import React from "react";
import Layout from "./Layout.jsx";
import { useReferralCapture } from "@/hooks/useReferralCode";
import { usePrivacyChoiceSync } from '@/hooks/usePrivacyChoiceSync';
import UpdateAvailableBanner from "@/components/UpdateAvailableBanner";
import { BookingFlowProvider } from "@/context/BookingFlowContext";
import { trackPageView, installClickTracking } from "@/lib/activityTracker";
import { installKeyboardAvoidance } from "@/lib/keyboardAvoidance";

import Upload from "./Upload";
import Dashboard from "./Dashboard";
import Home from "./Home";
import Profile from "./Profile";
import Analytics from "./Analytics";
import Users from "./Users";
import UserDetails from "./UserDetails";
import GoogleFlightsSearch from "./GoogleFlightsSearch";
import FlightOptions from "./FlightOptions";
import SelectSeats from "./SelectSeats";
import Stays from "./Stays";
import Review from "./Review";
import Book from "./Book";
import Carts from "./Carts";
import EditCart from "./EditCart";
import Accounting from "./Accounting";
import AccountingRewards from "./AccountingRewards";
import Settings from "./Settings";
import Investor from "./Investor";
import Marketing from "./Marketing";
import DestinationContent from "./DestinationContent";
import DiscountCodes from "./DiscountCodes";
import EarnCredit from "./EarnCredit";
import EarnCreditReview from "./EarnCreditReview";
import ResetPassword from "./ResetPassword";
import AuthCallback from "./AuthCallback";
import Terms from "./Terms";
import Privacy from "./Privacy";
import PrivacyChoices from "./PrivacyChoices";
import Contact from "./Contact";
import HelpHome from "./help/HelpHome";
import HelpSectionPage from "./help/HelpSectionPage";
import HelpArticlePage from "./help/HelpArticlePage";
import SecurityHome from "./security/SecurityHome";
import SecurityPage from "./security/SecurityPage";
import Support from "./Support";
import CustomerStories from "./CustomerStories";
import Press from "./Press";
import Newsroom from "./Newsroom";
import OurCharter from "./OurCharter";
import AboutUs from "./AboutUs";
import Careers from "./Careers";
import CareerDetail from "./CareerDetail";
import Rewards from "./Rewards";
import IndexMinimal from "./IndexMinimal";
import HowItWorks from "./HowItWorks";
import ErrorLog from "./ErrorLog";
import Explore from "./Explore";
import TestPage from "./TestPage";
import Guides from "./Guides";
import BlackList from "./BlackList";
import PastDue from "./PastDue";
import Collections from "./Collections";
import RiskInsights from "./RiskInsights";
import RiskReview from "./RiskReview";
import SettlementDesk from "./SettlementDesk";
import CashLadder from "./CashLadder";
import PortfolioIntelligence from "./PortfolioIntelligence";
import ModelPerformance from "./ModelPerformance";
import Ad from "./Ad";
import AdCapital from "./AdCapital";
import ReserveLanding from "./ReserveLanding";
import ReserveDashboard from "./ReserveDashboard";
import ReserveTerms from "./ReserveTerms";
import ReserveCheckout from "./ReserveCheckout";
import ReserveSwitchReview from "./ReserveSwitchReview";
import ChangeFlightReview from "./ChangeFlightReview";
import Map from "./Map";
import LiveFlights from "./LiveFlights";
import InvestorPitch from "./InvestorPitch";
import CapitalCheckoutStatus from "./CapitalCheckoutStatus";
import Activity from "./Activity";
import Staff from "./Staff";
import InquiriesDesk from "./InquiriesDesk";
import Brain from "./Brain";
import BookingRefCopy from "./BookingRefCopy";
import OfflineBanner from "@/components/OfflineBanner";
import FaceIdLock from "@/components/native/FaceIdLock";
import NativePushBridge from "@/components/native/NativePushBridge";

// SEO Articles
import FintechInstallmentFlightBooking from "./articles/FintechInstallmentFlightBooking";
import FlightInstallmentsBNPLPaymentOptions from "./articles/FlightInstallmentsBNPLPaymentOptions";
import FlightLayawayVsBNPL from "./articles/FlightLayawayVsBNPL";
import LockFlightPriceWithDeposit from "./articles/LockFlightPriceWithDeposit";
import PayFlightsWithGiftCardsCredits from "./articles/PayFlightsWithGiftCardsCredits";
import SemiPrivateFlightsBNPL from "./articles/SemiPrivateFlightsBNPL";
import DeferaVsCompetitors from "./articles/DeferaVsCompetitors";
import FastestFlightSearchEngines from "./articles/FastestFlightSearchEngines";
import GuideArticle from "./articles/GuideArticle";

import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { legacyFlightSearchPath } from '@/lib/legacySearchRedirect';
import SeoDefaults from '@/components/SeoDefaults';
import { HideNavProvider } from '@/context/HideNavContext';

// Memoize key route components to prevent unnecessary re-renders during animations
const MemoizedHome = React.memo(Home);
const MemoizedReview = React.memo(Review);
const MemoizedFlightOptions = React.memo(FlightOptions);
const MemoizedGoogleFlightsSearch = React.memo(GoogleFlightsSearch);
const MemoizedDashboard = React.memo(Dashboard);

const PAGES = {
    
    Upload: Upload,
    
    Dashboard: Dashboard,
    
    Home: Home,
    
    Profile: Profile,
    
    Analytics: Analytics,
    
    Users: Users,
    
    UserDetails: UserDetails,
    
    // Keyed for /search (the page name Layout gets); the page is GoogleFlightsSearch.
    Search: GoogleFlightsSearch,
    
    GoogleFlights: GoogleFlightsSearch,
    
    Review: Review,
    
    Book: Book,
    
    Carts: Carts,
    
    EditCart: EditCart,
    
    Settings: Settings,
    
}

/** The retired /flightsearch page: old links open /search with the same trip. */
function LegacyFlightSearchRedirect() {
    const location = useLocation();
    return <Navigate to={legacyFlightSearchPath(location.search)} replace />;
}

function _getCurrentPage(url) {
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }
    let urlLastPart = url.split('/').pop();
    if (urlLastPart.includes('?')) {
        urlLastPart = urlLastPart.split('?')[0];
    }

    const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === urlLastPart.toLowerCase());
    return pageName || Object.keys(PAGES)[0];
}

// Create a wrapper component that uses useLocation inside the Router context
function PagesContent() {
    const location = useLocation();
    const currentPage = _getCurrentPage(location.pathname);

    // Behavioral telemetry for Ezra (server/ezra-insights.js): one page_view
    // per SPA navigation, plus a single delegated click listener. Both are
    // fire-and-forget and can never affect the UI.
    React.useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
    React.useEffect(() => installClickTracking(), []);

    // Every page opens at its top, inside Layout or not. Layout used to do
    // this for its own pages only, so a page outside it (the landing page,
    // How it Works, Help, Security...) opened at the previous page's scroll
    // position, its nav already up under the status bar, where the installed
    // iPhone app fades it out.
    React.useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

    // Phones and the iOS app: keep the field being typed in above the
    // on-screen keyboard (src/lib/keyboardAvoidance.js).
    React.useEffect(() => installKeyboardAvoidance(), []);

    // Creator referral links (?ref=CODE). This MUST live here rather than in
    // Layout: the landing page and every public content page render OUTSIDE
    // Layout, and `/` is exactly where a link-in-bio points. Mounted in Layout
    // it silently captured nothing for the one route that matters most.
    useReferralCapture();

    // "Do Not Sell or Share" / Global Privacy Control reaches a signed-in
    // customer's account, so their bookings stop going to ad measurement.
    usePrivacyChoiceSync();

    return (
        <>
            <SeoDefaults />
            {/* Offers a refresh when production is on a newer build than this
                tab. Here, not in Layout, for the same reason as the referral
                capture above: `/` and the public pages render outside Layout,
                and a stale tab on the landing page is still a stale tab. */}
            <UpdateAvailableBanner />
            {/* iOS app only: says why nothing loads without signal. */}
            <OfflineBanner />
            {/* iOS app only: the optional Face ID lock, and notification taps
                opening their trip. */}
            <FaceIdLock />
            <NativePushBridge />
            <Routes>
            {/* Landing Page - Default Home - No Layout */}
            <Route path="/test" element={<TestPage />} />
            <Route path="/" element={<IndexMinimal />} />
            <Route path="/index" element={<IndexMinimal />} />
            
            {/* Public Content Pages - No Layout */}
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-choices" element={<PrivacyChoices />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/customers" element={<CustomerStories />} />
            <Route path="/press" element={<Press />} />
            <Route path="/newsroom" element={<Newsroom />} />
            <Route path="/charter" element={<OurCharter />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:slug" element={<CareerDetail />} />
            
            {/* Help Center - public documentation hub, outside the sign-in wall
                so visitors and search engines can read every article. */}
            <Route path="/help" element={<HelpHome />} />
            <Route path="/help/:sectionId" element={<HelpSectionPage />} />
            <Route path="/help/:sectionId/:articleSlug" element={<HelpArticlePage />} />

            {/* Security Center - public: how we protect accounts, payments and
                data, avoiding scams, and the vulnerability disclosure policy. */}
            <Route path="/security" element={<SecurityHome />} />
            <Route path="/security/:slug" element={<SecurityPage />} />

            {/* Guides Page */}
            <Route path="/guides" element={<Guides />} />
            
            {/* SEO Articles - Under /guides/ prefix */}
            <Route path="/guides/fastest-flight-search-engines" element={<FastestFlightSearchEngines />} />
            <Route path="/guides/fintech-installment-flight-booking" element={<FintechInstallmentFlightBooking />} />
            <Route path="/guides/flight-installments-bnpl-payment-options" element={<FlightInstallmentsBNPLPaymentOptions />} />
            <Route path="/guides/flight-layaway-vs-bnpl" element={<FlightLayawayVsBNPL />} />
            <Route path="/guides/lock-flight-price-with-deposit" element={<LockFlightPriceWithDeposit />} />
            <Route path="/guides/pay-flights-with-gift-cards-credits" element={<PayFlightsWithGiftCardsCredits />} />
            <Route path="/guides/semi-private-flights-bnpl" element={<SemiPrivateFlightsBNPL />} />
            <Route path="/guides/defera-vs-affirm-klarna-uplift" element={<DeferaVsCompetitors />} />
            {/* Guides written as data (src/content/guides); the named routes above win over this one */}
            <Route path="/guides/:slug" element={<GuideArticle />} />
            
            {/* Admin-only Ad page - No Layout, no SEO */}
            <Route path="/ad" element={<Ad />} />
            <Route path="/ad/capital" element={<AdCapital />} />
            
            {/* Investor Pitch - PIN-protected, no Layout, no SEO */}
            <Route path="/pitch" element={<InvestorPitch />} />

            {/* The Brain - admin-only living neural map of the whole system.
                Deliberately outside Layout: full-screen, no nav, no tabs. */}
            <Route path="/brain" element={<Brain />} />
            <Route path="/Brain" element={<Brain />} />

            {/* One-tap copy for the booking reference in the confirmation
                email (email apps can't copy). No Layout, no auth, no fetch. */}
            <Route path="/ref/:code" element={<BookingRefCopy />} />

            {/* Defera Reserve - Public Landing Page */}
            <Route path="/reserve" element={<ReserveLanding />} />
            <Route path="/reserve/terms" element={<ReserveTerms />} />
            <Route path="/reserve/checkout" element={<ReserveCheckout />} />
            <Route path="/reserve/switch-review" element={<ReserveSwitchReview />} />
            
            {/* All other routes - Wrapped in Layout (public + authenticated) */}
            <Route path="*" element={
                <HideNavProvider>
                    <Layout currentPageName={currentPage}>
                    <Routes>
                        {/* Public Flight Booking Flow - No auth required */}
                        <Route path="/search" element={<MemoizedGoogleFlightsSearch />} />
                        <Route path="/flightoptions" element={<MemoizedFlightOptions />} />
                        <Route path="/selectseats" element={<SelectSeats />} />
                        <Route path="/Review" element={<MemoizedReview />} />
                        <Route path="/explore" element={<Explore />} />
                        
                        {/* Authenticated Routes - Auth required */}
                        <Route path="/home" element={<MemoizedHome />} />
                        <Route path="/Dashboard" element={<MemoizedDashboard />} />
                        <Route path="/dashboard" element={<MemoizedDashboard />} />
                        <Route path="/trips" element={<MemoizedDashboard />} />
                        <Route path="/trips/:id" element={<MemoizedDashboard />} />
                        <Route path="/trips/change-flight/review" element={<ChangeFlightReview />} />
                        {/* Capital checkout status — recovery page customers land on if their checkout
                            drops between Stripe charging the card and our webhook creating the booking row.
                            Polls /defera-capital/checkout-status and silently redirects on confirmed. */}
                        <Route path="/capital-checkout-status" element={<CapitalCheckoutStatus />} />
                        <Route path="/CapitalCheckoutStatus" element={<CapitalCheckoutStatus />} />
                        <Route path="/Home" element={<MemoizedHome />} />
                        <Route path="/Profile" element={<Profile />} />
                        <Route path="/Analytics" element={<Analytics />} />
                        <Route path="/Users" element={<Users />} />
                        <Route path="/blacklist" element={<BlackList />} />
                        {/* Backwards compatibility for older links */}
                        <Route path="/BlackList" element={<BlackList />} />
                        <Route path="/past-due" element={<PastDue />} />
                        <Route path="/PastDue" element={<PastDue />} />
                        <Route path="/collections" element={<Collections />} />
                        <Route path="/Collections" element={<Collections />} />
                        <Route path="/RiskInsights" element={<RiskInsights />} />
                        <Route path="/riskinsights" element={<RiskInsights />} />
                        <Route path="/RiskReview" element={<RiskReview />} />
                        <Route path="/riskreview" element={<RiskReview />} />
                        <Route path="/risk-review" element={<RiskReview />} />
                        <Route path="/SettlementDesk" element={<SettlementDesk />} />
                        <Route path="/settlementdesk" element={<SettlementDesk />} />
                        <Route path="/settlement-desk" element={<SettlementDesk />} />
                        <Route path="/CashLadder" element={<CashLadder />} />
                        <Route path="/cashladder" element={<CashLadder />} />
                        <Route path="/cash-ladder" element={<CashLadder />} />
                        <Route path="/PortfolioIntelligence" element={<PortfolioIntelligence />} />
                        <Route path="/portfoliointelligence" element={<PortfolioIntelligence />} />
                        <Route path="/portfolio-intelligence" element={<PortfolioIntelligence />} />
                        <Route path="/ModelPerformance" element={<ModelPerformance />} />
                        <Route path="/modelperformance" element={<ModelPerformance />} />
                        <Route path="/model-performance" element={<ModelPerformance />} />
                        <Route path="/UserDetails" element={<UserDetails />} />
                        <Route path="/flightsearch" element={<LegacyFlightSearchRedirect />} />
                        <Route path="/stays" element={<Stays />} />
                        <Route path="/Book" element={<Book />} />
                        <Route path="/Carts" element={<Carts />} />
                        <Route path="/EditCart" element={<EditCart />} />
                        <Route path="/Accounting" element={<Accounting />} />
                        <Route path="/AccountingRewards" element={<AccountingRewards />} />
                        <Route path="/Settings" element={<Settings />} />
                        <Route path="/Investor" element={<Investor />} />
                        <Route path="/Marketing" element={<Marketing />} />
                        <Route path="/discount-codes" element={<DiscountCodes />} />
                        <Route path="/DiscountCodes" element={<DiscountCodes />} />
                        <Route path="/earn-review" element={<EarnCreditReview />} />
                        <Route path="/destination-content" element={<DestinationContent />} />
                        <Route path="/DestinationContent" element={<DestinationContent />} />
                        <Route path="/support" element={<Support />} />
                        <Route path="/Support" element={<Support />} />
                        <Route path="/rewards" element={<Rewards />} />
                        <Route path="/earn" element={<EarnCredit />} />
                        {/* Activity — chronological feed of customer booking events
                            (payments, flight changes, cancellations, schedule changes,
                            card updates, credits). Substitutes for the transactional
                            emails the NMI track doesn't send. */}
                        <Route path="/activity" element={<Activity />} />
                        <Route path="/Activity" element={<Activity />} />
                        {/* Staff — admin-only home for Defera's AI staff
                            (Tommy the concierge + Stephanie, his quality
                            manager). Server routes are requireAdmin-gated;
                            the page renders a restricted state for
                            non-admins. */}
                        <Route path="/staff" element={<Staff />} />
                        <Route path="/Staff" element={<Staff />} />
                        {/* Inquiries desk — the sit-down dashboard for the
                            concierge inquiry queue: split-view inbox with
                            each customer's chat transcript for context. */}
                        <Route path="/staff/inquiries" element={<InquiriesDesk />} />
                        <Route path="/Staff/Inquiries" element={<InquiriesDesk />} />
                        <Route path="/ErrorLog" element={<ErrorLog />} />
                        
                        {/* Defera Reserve - Authenticated Dashboard */}
                        <Route path="/app/reserve" element={<ReserveDashboard />} />
                        
                        {/* Admin Map */}
                        <Route path="/map" element={<Map />} />
                        {/* Live Flights — admins: every traveler in the air now (server/live-flights.js). */}
                        <Route path="/live-flights" element={<LiveFlights />} />
                    </Routes>
                    </Layout>
                </HideNavProvider>
            } />
            </Routes>
        </>
    );
}

export default function Pages() {
    return (
        <BookingFlowProvider>
            <Router>
                <PagesContent />
            </Router>
        </BookingFlowProvider>
    );
}